import { useAuth } from "@/contexts/auth-context";
import { useTheme } from "@/contexts/theme-context";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useLocation, useNavigate } from "react-router-dom";
import { Settings, Sun, Moon, MoreVertical, LogOut, FlaskConical, HelpCircle, Activity, GitCommitHorizontal, Workflow, Briefcase, Wrench, Search } from "lucide-react";
import flatfileLogo from "@/public/images/flatfile-logo.svg";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";

export function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasRunningTests, setHasRunningTests] = useState(false);

  if (!user) return null;

  const isAdmin = user.role === "admin";
  const currentPath = location.pathname;

  // Check for running tests periodically
  useEffect(() => {
    const checkRunningTests = async () => {
      try {
        const response = await api.get<{ hasRunningTests: boolean }>("/tests/running/status");
        setHasRunningTests(response.hasRunningTests);
      } catch (error) {
        console.error("Failed to check running tests status:", error);
        setHasRunningTests(false);
      }
    };

    // Initial check
    checkRunningTests();

    // Set up polling every 10 seconds
    const interval = setInterval(checkRunningTests, 10000);

    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    {
      label: "Test Management",
      path: "/app/tests",
      icon: <FlaskConical className="w-5 h-5" />
    },
    {
      label: "Running Tests",
      path: "/app/running-tests",
      icon: <Activity className="w-5 h-5" />
    },
    {
      label: "Reporting",
      path: "/app/jobs",
      icon: <Briefcase className="w-5 h-5" />
    },
    {
      label: "Commits",
      path: "/app/commits",
      icon: <GitCommitHorizontal className="w-5 h-5" />
    },
    {
      label: "Mappings",
      path: "/app/mappings",
      icon: <Workflow className="w-5 h-5" />
    },
    {
      label: "Tools",
      path: "/app/tools",
      icon: <Wrench className="w-5 h-5" />
    },
    {
      label: "Job Finder",
      path: "/app/job-finder",
      icon: <Search className="w-5 h-5" />
    },
    {
      label: "Help",
      path: "/app/help",
      icon: <HelpCircle className="w-5 h-5" />
    }
  ];

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleThemeChange = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
  };

  const themeIcon = theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />;

  return (
    <div className="w-64 bg-background border-r border-border flex flex-col min-h-screen">
      {/* Logo */}
      <div className="p-4 pl-6 border-b border-border h-[65px]">
        <div className="flex items-left">
          <img 
            src={flatfileLogo} 
            alt="Flatfile" 
            className="h-8 w-auto rounded-sm"
          />
          <span className="text-lg font-bold mt-[2px] ml-3">Flatfile x ADP</span>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-full cursor-pointer flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
              currentPath.startsWith(item.path)
                ? "bg-muted text-foreground"
                : "hover:bg-muted text-foreground"
            }`}
          >
            {item.icon}
            <span className="text-sm flex-1">{item.label}</span>
            {item.label === "Running Tests" && hasRunningTests && (
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            )}
          </button>
        ))}
      </nav>

      {/* Admin Section - Only show for admins */}
        {isAdmin && (
          <div className="border-t border-border p-4">
            <button
              onClick={() => navigate("/app/admin/logs")}
              className={`w-full cursor-pointer flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                currentPath.startsWith("/app/admin")
                  ? "bg-muted text-foreground"
                  : "hover:bg-muted text-foreground"
              }`}
            >
              <Settings className="w-5 h-5" />
              <span className="text-sm">Admin</span>
            </button>
          </div>
        )}

      {/* User Section */}
      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-sm">
              {getInitials(user.name)}
            </div>
            
            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>

          {/* Three Dots Menu */}
          <DropdownMenu
            trigger={<button className="h-8 px-2 text-xs cursor-pointer rounded-md text-foreground hover:bg-muted/80"><MoreVertical className="w-4 h-4" /></button>}
            align="right"
            side="top"
            triggerAsChild
            contentClassName="w-56"
          >
            <div className="px-3 py-2 text-[11px] text-muted-foreground">Preferences</div>
            <DropdownMenuItem onClick={() => handleThemeChange(theme === "dark" ? "light" : "dark")} className="cursor-pointer">
              {themeIcon}
              <span className="text-sm">{theme === "dark" ? "Light mode" : "Dark mode"}</span>
            </DropdownMenuItem>
            <div className="my-1 border-t border-border" />
            <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Logout</span>
            </DropdownMenuItem>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
